/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useState, useEffect, useCallback } from "react"
import { observer } from "mobx-react"
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    IconButton,
    Paper,
    Divider,
    Alert,
} from "@mui/material"
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material"

import { ChatbotTestConfigurationService } from "src/api/services/ChatbotTestConfigurationService"
import type { adminv3_ChatbotTestConfigurationResponse } from "src/api/models/adminv3_ChatbotTestConfigurationResponse"

import { DEFAULT_ACCESS_GROUP } from "src/config"

import { GlobalStore } from "src/store"
import { useStore } from "src/store/lib/useStore"

const RESPONSE_TYPES = [
    { value: "first_option_text", label: "First Option Text" },
    { value: "second_option_text", label: "Second Option Text" },
    { value: "third_option_text", label: "Third Option Text" },
    {
        value: "confirm_contact_details",
        label: "Confirm Contact Details As-Is",
    },
    { value: "edit_contact_details", label: "Edit Contact Details" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "continue", label: "Continue" },
    { value: "skip", label: "Skip" },
    { value: "custom", label: "Custom Text" },
]

const MESSAGE_TYPES = [
    "INTRODUCTION",
    "DISPATCH",
    "CHAT_NO_ANSWER",
    "USER_INPUT",
    "REASON_REQUEST",
    "CHAT_ANSWER",
    "CHAT_ANSWER_OPTIONS",
    "CHAT_ANSWER_FILE",
    "QUESTION",
    "CONFIRM_CONTACT_DETAILS",
]

const ENTRY_POINTS = ["qa_agent", "dynamic_form_agent"]

interface IRuleCondition {
    message_type?: string
    message_contains?: string
}

interface IRuleResponse {
    type: string
    value?: string
}

interface IRule {
    id: string
    condition: IRuleCondition
    response: IRuleResponse
    description: string
}

interface IEndConditionCondition {
    message_type?: string
    message_contains?: string
}

interface IEndCondition {
    id: string
    condition: IEndConditionCondition
    result: "pass" | "fail"
    description: string
}

interface IEntryPointConfig {
    entry_point?: string
    form_id?: number
}

interface IRulesConfig {
    entry_point_config?: IEntryPointConfig
    rules?: Array<{
        condition?: IRuleCondition
        response?: string | Record<string, unknown>
        description?: string
    }>
    end_conditions?: Array<{
        condition?: IEndConditionCondition
        result?: "pass" | "fail"
        description?: string
    }>
    settings?: {
        max_turns?: number
        max_empty_responses?: number
        min_turns_before_timeout?: number
    }
}

interface IRulesEditorWithCRUDProps {
    selectedConfiguration: adminv3_ChatbotTestConfigurationResponse | null
    onConfigurationChange: (
        config: adminv3_ChatbotTestConfigurationResponse | null,
    ) => void
    onConfigurationsUpdate: () => void
}

export const RulesEditorWithCRUD: React.FC<IRulesEditorWithCRUDProps> =
    observer(
        ({
            selectedConfiguration,
            onConfigurationChange,
            onConfigurationsUpdate,
        }) => {
            const globalStore = useStore(GlobalStore)
            const [configName, setConfigName] = useState("")
            const [configDescription, setConfigDescription] = useState("")
            const [entryPoint, setEntryPoint] = useState("qa_agent")
            const [formId, setFormId] = useState<number | "">(1)
            const [rules, setRules] = useState<IRule[]>([])
            const [endConditions, setEndConditions] = useState<IEndCondition[]>(
                [],
            )
            const [isLoading, setIsLoading] = useState(false)
            const [error, setError] = useState<string | null>(null)
            const [selectedAccessGroupId, setSelectedAccessGroupId] =
                useState<number>(
                    globalStore.session.accessGroup?.id ||
                        DEFAULT_ACCESS_GROUP.id,
                )

            // Environment configuration
            const [envTenantId, setEnvTenantId] = useState<string>("")
            const [envSubdomain, setEnvSubdomain] = useState<string>("")
            const [envApiEnvironment, setEnvApiEnvironment] =
                useState<string>("")

            // Reset form when selected configuration changes
            useEffect(() => {
                if (selectedConfiguration != null) {
                    setConfigName(selectedConfiguration.name ?? "")
                    setConfigDescription(
                        selectedConfiguration.description ?? "",
                    )
                    setSelectedAccessGroupId(
                        selectedConfiguration.access_group_id ??
                            globalStore.session.accessGroup?.id ??
                            DEFAULT_ACCESS_GROUP.id,
                    )

                    const config =
                        (selectedConfiguration.rules_config as unknown as IRulesConfig) ??
                        ({} as IRulesConfig)
                    if (config != null) {
                        setEntryPoint(
                            (config.entry_point_config?.entry_point ??
                                "qa_agent") as string,
                        )
                        setFormId(
                            (config.entry_point_config?.form_id ?? 1) as number,
                        )

                        // Parse rules
                        const configRules = config.rules ?? []
                        setRules(
                            configRules.map((rule, index: number) => ({
                                id: `rule-${index}`,
                                condition: rule.condition ?? {},
                                response: {
                                    type:
                                        typeof rule.response === "string"
                                            ? RESPONSE_TYPES.find(
                                                  (rt) =>
                                                      rt.value ===
                                                      rule.response,
                                              ) != null
                                                ? (rule.response as string)
                                                : "custom"
                                            : "custom",
                                    value:
                                        typeof rule.response === "string"
                                            ? RESPONSE_TYPES.find(
                                                  (rt) =>
                                                      rt.value ===
                                                      rule.response,
                                              ) != null
                                                ? undefined
                                                : (rule.response as string)
                                            : (rule.response as
                                                  | string
                                                  | undefined),
                                },
                                description: rule.description ?? "",
                            })),
                        )

                        // Parse end conditions
                        const configEndConditions = config.end_conditions ?? []
                        setEndConditions(
                            configEndConditions.map(
                                (condition, index: number) => ({
                                    id: `end-${index}`,
                                    condition: (condition.condition ??
                                        {}) as IEndConditionCondition,
                                    result: (condition.result ?? "fail") as
                                        | "pass"
                                        | "fail",
                                    description: condition.description ?? "",
                                }),
                            ),
                        )
                    }

                    // Parse test config (environment settings)
                    const testConfig =
                        selectedConfiguration.test_config as unknown as {
                            tenant_id?: string
                            subdomain?: string
                            api_environment?: string
                        } | null
                    if (testConfig) {
                        setEnvTenantId(testConfig.tenant_id ?? "")
                        setEnvSubdomain(testConfig.subdomain ?? "")
                        setEnvApiEnvironment(testConfig.api_environment ?? "")
                    } else {
                        setEnvTenantId("")
                        setEnvSubdomain("")
                        setEnvApiEnvironment("")
                    }
                } else {
                    // Reset form
                    setConfigName("")
                    setConfigDescription("")
                    setEntryPoint("qa_agent")
                    setFormId(1)
                    setRules([])
                    setEndConditions([])
                    setSelectedAccessGroupId(
                        globalStore.session.accessGroup?.id ||
                            DEFAULT_ACCESS_GROUP.id,
                    )
                    setEnvTenantId("")
                    setEnvSubdomain("")
                    setEnvApiEnvironment("")
                }
            }, [selectedConfiguration, globalStore.session.accessGroup])

            const addRule = useCallback(() => {
                const newRule: IRule = {
                    id: `rule-${Date.now()}`,
                    condition: {},
                    response: { type: "custom", value: "" },
                    description: "",
                }
                setRules((prev) => [...prev, newRule])
            }, [])

            const updateRule = useCallback(
                (id: string, field: keyof IRule, value: unknown) => {
                    setRules((prev) =>
                        prev.map((rule) => {
                            if (rule.id !== id) return rule
                            // Narrow value assignment safely
                            if (field === "condition") {
                                return {
                                    ...rule,
                                    condition: value as IRuleCondition,
                                }
                            }
                            if (field === "response") {
                                return {
                                    ...rule,
                                    response: value as IRuleResponse,
                                }
                            }
                            if (field === "description") {
                                return {
                                    ...rule,
                                    description: String(value ?? ""),
                                }
                            }
                            return rule
                        }),
                    )
                },
                [],
            )

            const deleteRule = useCallback((id: string) => {
                setRules((prev) => prev.filter((rule) => rule.id !== id))
            }, [])

            const addEndCondition = useCallback(() => {
                const newCondition: IEndCondition = {
                    id: `end-${Date.now()}`,
                    condition: { message_contains: "" },
                    result: "fail",
                    description: "",
                }
                setEndConditions((prev) => [...prev, newCondition])
            }, [])

            const updateEndCondition = useCallback(
                (id: string, field: keyof IEndCondition, value: unknown) => {
                    setEndConditions((prev) =>
                        prev.map((condition) => {
                            if (condition.id !== id) return condition
                            if (field === "condition") {
                                return {
                                    ...condition,
                                    condition: value as IEndConditionCondition,
                                }
                            }
                            if (field === "result") {
                                return {
                                    ...condition,
                                    result:
                                        (value as "pass" | "fail") ?? "fail",
                                }
                            }
                            if (field === "description") {
                                return {
                                    ...condition,
                                    description: String(value ?? ""),
                                }
                            }
                            return condition
                        }),
                    )
                },
                [],
            )

            const deleteEndCondition = useCallback((id: string) => {
                setEndConditions((prev) =>
                    prev.filter((condition) => condition.id !== id),
                )
            }, [])

            const generateConfigJSON = useCallback(() => {
                const rulesConfig = rules.map((rule) => ({
                    condition: rule.condition,
                    response:
                        rule.response.type === "custom"
                            ? rule.response.value
                            : rule.response.type,
                    description: rule.description,
                }))

                const endConditionsConfig = endConditions.map((condition) => ({
                    condition: condition.condition,
                    result: condition.result,
                    description: condition.description,
                }))

                const entryPointConfig: IEntryPointConfig = {
                    entry_point: entryPoint,
                }

                // Add form_id for dynamic_form_agent
                if (
                    entryPoint === "dynamic_form_agent" &&
                    formId !== "" &&
                    formId !== 0
                ) {
                    entryPointConfig.form_id =
                        typeof formId === "string"
                            ? parseInt(formId, 10)
                            : formId
                }

                return {
                    entry_point_config: entryPointConfig,
                    rules: rulesConfig,
                    end_conditions: endConditionsConfig,
                    settings: {
                        max_empty_responses: 3,
                        min_turns_before_timeout: 10,
                    },
                }
            }, [entryPoint, formId, rules, endConditions])

            const handleSave = useCallback(async () => {
                if (configName.trim().length === 0) {
                    setError("Configuration name is required")
                    return
                }

                if (
                    entryPoint === "dynamic_form_agent" &&
                    (formId === "" || formId === 0)
                ) {
                    setError("Form ID is required for dynamic form agent")
                    return
                }

                setIsLoading(true)
                setError(null)

                try {
                    // Build test_config only if any environment values are provided
                    const testConfig =
                        envTenantId || envSubdomain || envApiEnvironment
                            ? {
                                  tenant_id: envTenantId || undefined,
                                  subdomain: envSubdomain || undefined,
                                  api_environment:
                                      envApiEnvironment || undefined,
                              }
                            : undefined

                    const configData = {
                        name: configName,
                        description: configDescription,
                        rules_config: generateConfigJSON(),
                        test_config: testConfig,
                        access_group_id: selectedAccessGroupId,
                    }

                    let result: adminv3_ChatbotTestConfigurationResponse
                    if (selectedConfiguration != null) {
                        // Update existing
                        result =
                            await ChatbotTestConfigurationService.putV3AdminChatbotTestConfiguration(
                                {
                                    id: selectedConfiguration.id as number,
                                    request: configData,
                                },
                            )
                    } else {
                        // Create new
                        result =
                            await ChatbotTestConfigurationService.postV3AdminChatbotTestConfiguration(
                                {
                                    request: configData,
                                },
                            )
                    }

                    onConfigurationChange(result)
                    onConfigurationsUpdate()
                } catch (err) {
                    setError(
                        err instanceof Error
                            ? err.message
                            : "Failed to save configuration",
                    )
                } finally {
                    setIsLoading(false)
                }
            }, [
                configName,
                configDescription,
                generateConfigJSON,
                selectedAccessGroupId,
                selectedConfiguration,
                entryPoint,
                formId,
                envTenantId,
                envSubdomain,
                envApiEnvironment,
                onConfigurationChange,
                onConfigurationsUpdate,
            ])

            return (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {selectedConfiguration != null ? "Edit" : "Create"}{" "}
                            Test Configuration
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Configuration Name"
                                    value={configName}
                                    onChange={(e) =>
                                        setConfigName(e.target.value)
                                    }
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Access Group</InputLabel>
                                    <Select
                                        value={selectedAccessGroupId}
                                        onChange={(e) =>
                                            setSelectedAccessGroupId(
                                                e.target.value as number,
                                            )
                                        }
                                        label="Access Group"
                                    >
                                        {globalStore.session.accessGroups.map(
                                            (group) => (
                                                <MenuItem
                                                    key={group.id}
                                                    value={group.id}
                                                >
                                                    {group.name}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label="Description"
                                    value={configDescription}
                                    onChange={(e) =>
                                        setConfigDescription(e.target.value)
                                    }
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Entry Point</InputLabel>
                                    <Select
                                        value={entryPoint}
                                        onChange={(e) =>
                                            setEntryPoint(e.target.value)
                                        }
                                        label="Entry Point"
                                    >
                                        {ENTRY_POINTS.map((ep) => (
                                            <MenuItem key={ep} value={ep}>
                                                {ep}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {entryPoint === "dynamic_form_agent" && (
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth margin="normal">
                                        <TextField
                                            label="Form ID"
                                            type="number"
                                            value={formId}
                                            onChange={(e) => {
                                                const value = e.target.value
                                                setFormId(
                                                    value === ""
                                                        ? ""
                                                        : parseInt(value),
                                                )
                                            }}
                                            helperText="Required for dynamic form agent"
                                            required
                                        />
                                    </FormControl>
                                </Grid>
                            )}
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        {/* Environment Configuration Section */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Environment Configuration
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2 }}
                            >
                                Optional: Configure specific environment
                                settings. If not set, runtime defaults will be
                                used.
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Environment</InputLabel>
                                        <Select
                                            value={envApiEnvironment}
                                            onChange={(e) =>
                                                setEnvApiEnvironment(
                                                    e.target.value,
                                                )
                                            }
                                            label="Environment"
                                        >
                                            <MenuItem value="">
                                                Use runtime default
                                            </MenuItem>
                                            <MenuItem value="stage">
                                                Stage
                                            </MenuItem>
                                            <MenuItem value="dev">
                                                Development
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Tenant ID"
                                        value={envTenantId}
                                        onChange={(e) =>
                                            setEnvTenantId(e.target.value)
                                        }
                                        placeholder="51002"
                                        helperText="Leave empty to use runtime default"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Subdomain"
                                        value={envSubdomain}
                                        onChange={(e) =>
                                            setEnvSubdomain(e.target.value)
                                        }
                                        placeholder="stage-prodteam.avy.se"
                                        helperText="Leave empty to use runtime default"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Rules Section */}
                        <Box sx={{ mb: 3 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6">Rules</Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addRule}
                                    variant="outlined"
                                >
                                    Add Rule
                                </Button>
                            </Box>

                            {rules.map((rule) => (
                                <Paper key={rule.id} sx={{ p: 3, mb: 2 }}>
                                    <Grid container spacing={3}>
                                        {/* First row: Main condition and response fields */}
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>
                                                    Message Type
                                                </InputLabel>
                                                <Select
                                                    value={
                                                        rule.condition
                                                            .message_type || ""
                                                    }
                                                    onChange={(e) =>
                                                        updateRule(
                                                            rule.id,
                                                            "condition",
                                                            {
                                                                ...rule.condition,
                                                                message_type:
                                                                    e.target
                                                                        .value,
                                                            },
                                                        )
                                                    }
                                                    label="Message Type"
                                                >
                                                    {MESSAGE_TYPES.map(
                                                        (type) => (
                                                            <MenuItem
                                                                key={type}
                                                                value={type}
                                                            >
                                                                {type}
                                                            </MenuItem>
                                                        ),
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <FormControl fullWidth>
                                                <InputLabel>
                                                    Response Type
                                                </InputLabel>
                                                <Select
                                                    value={rule.response.type}
                                                    onChange={(e) =>
                                                        updateRule(
                                                            rule.id,
                                                            "response",
                                                            {
                                                                type: e.target
                                                                    .value,
                                                                value: rule
                                                                    .response
                                                                    .value,
                                                            },
                                                        )
                                                    }
                                                    label="Response Type"
                                                >
                                                    {RESPONSE_TYPES.map(
                                                        (type) => (
                                                            <MenuItem
                                                                key={type.value}
                                                                value={
                                                                    type.value
                                                                }
                                                            >
                                                                {type.label}
                                                            </MenuItem>
                                                        ),
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            md={1}
                                            sx={{
                                                display: "flex",
                                                justifyContent: {
                                                    xs: "flex-start",
                                                    md: "center",
                                                },
                                                alignItems: "center",
                                            }}
                                        >
                                            <IconButton
                                                onClick={() =>
                                                    deleteRule(rule.id)
                                                }
                                                color="error"
                                                sx={{ mr: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>

                                        {/* Second row: Custom response text if needed */}
                                        {rule.response.type === "custom" && (
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Custom Response Text"
                                                    value={
                                                        rule.response.value ||
                                                        ""
                                                    }
                                                    onChange={(e) =>
                                                        updateRule(
                                                            rule.id,
                                                            "response",
                                                            {
                                                                ...rule.response,
                                                                value: e.target
                                                                    .value,
                                                            },
                                                        )
                                                    }
                                                    multiline
                                                    rows={2}
                                                />
                                            </Grid>
                                        )}

                                        {/* Third row: Description */}
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Description"
                                                value={rule.description}
                                                onChange={(e) =>
                                                    updateRule(
                                                        rule.id,
                                                        "description",
                                                        e.target.value,
                                                    )
                                                }
                                                multiline
                                                rows={2}
                                                placeholder="Describe what this rule does..."
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* End Conditions Section */}
                        <Box sx={{ mb: 3 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6">
                                    End Conditions
                                </Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addEndCondition}
                                    variant="outlined"
                                >
                                    Add End Condition
                                </Button>
                            </Box>

                            {endConditions.map((condition) => {
                                const conditionType = condition.condition
                                    .message_type
                                    ? "message_type"
                                    : "message_contains"

                                return (
                                    <Paper
                                        key={condition.id}
                                        sx={{ p: 3, mb: 2 }}
                                    >
                                        <Grid container spacing={3}>
                                            {/* First row: Condition type and message field */}
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>
                                                        Condition Type
                                                    </InputLabel>
                                                    <Select
                                                        value={conditionType}
                                                        onChange={(e) => {
                                                            const newType =
                                                                e.target.value
                                                            if (
                                                                newType ===
                                                                "message_type"
                                                            ) {
                                                                updateEndCondition(
                                                                    condition.id,
                                                                    "condition",
                                                                    {
                                                                        message_type:
                                                                            "CHAT_ANSWER",
                                                                        message_contains:
                                                                            undefined,
                                                                    },
                                                                )
                                                            } else {
                                                                updateEndCondition(
                                                                    condition.id,
                                                                    "condition",
                                                                    {
                                                                        message_type:
                                                                            undefined,
                                                                        message_contains:
                                                                            "",
                                                                    },
                                                                )
                                                            }
                                                        }}
                                                        label="Condition Type"
                                                    >
                                                        <MenuItem value="message_type">
                                                            Message Type
                                                        </MenuItem>
                                                        <MenuItem value="message_contains">
                                                            Message Contains
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                {conditionType ===
                                                "message_type" ? (
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            Message Type
                                                        </InputLabel>
                                                        <Select
                                                            value={
                                                                condition
                                                                    .condition
                                                                    .message_type ||
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                updateEndCondition(
                                                                    condition.id,
                                                                    "condition",
                                                                    {
                                                                        ...condition.condition,
                                                                        message_type:
                                                                            e
                                                                                .target
                                                                                .value,
                                                                    },
                                                                )
                                                            }
                                                            label="Message Type"
                                                        >
                                                            {MESSAGE_TYPES.map(
                                                                (type) => (
                                                                    <MenuItem
                                                                        key={
                                                                            type
                                                                        }
                                                                        value={
                                                                            type
                                                                        }
                                                                    >
                                                                        {type}
                                                                    </MenuItem>
                                                                ),
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                ) : (
                                                    <TextField
                                                        fullWidth
                                                        label="Message Contains"
                                                        value={
                                                            condition.condition
                                                                .message_contains ||
                                                            ""
                                                        }
                                                        onChange={(e) =>
                                                            updateEndCondition(
                                                                condition.id,
                                                                "condition",
                                                                {
                                                                    ...condition.condition,
                                                                    message_contains:
                                                                        e.target
                                                                            .value,
                                                                },
                                                            )
                                                        }
                                                        placeholder={
                                                            conditionType ===
                                                            "message_contains"
                                                                ? "Enter text to match..."
                                                                : ""
                                                        }
                                                    />
                                                )}
                                            </Grid>

                                            {/* Second row: Result and delete button */}
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>
                                                        Result
                                                    </InputLabel>
                                                    <Select
                                                        value={
                                                            condition.result ||
                                                            "fail"
                                                        }
                                                        onChange={(e) =>
                                                            updateEndCondition(
                                                                condition.id,
                                                                "result",
                                                                e.target.value,
                                                            )
                                                        }
                                                        label="Result"
                                                    >
                                                        <MenuItem value="pass">
                                                            ✅ Pass
                                                        </MenuItem>
                                                        <MenuItem value="fail">
                                                            ❌ Fail
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid
                                                item
                                                xs={12}
                                                md={6}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: {
                                                        xs: "flex-start",
                                                        md: "flex-end",
                                                    },
                                                    alignItems: "center",
                                                }}
                                            >
                                                <IconButton
                                                    onClick={() =>
                                                        deleteEndCondition(
                                                            condition.id,
                                                        )
                                                    }
                                                    color="error"
                                                    sx={{ mr: 1 }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Grid>

                                            {/* Second row: Description */}
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Description"
                                                    value={
                                                        condition.description
                                                    }
                                                    onChange={(e) =>
                                                        updateEndCondition(
                                                            condition.id,
                                                            "description",
                                                            e.target.value,
                                                        )
                                                    }
                                                    multiline
                                                    rows={2}
                                                    placeholder="Describe when this end condition should trigger..."
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                )
                            })}
                        </Box>

                        {/* JSON Preview Section */}
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" gutterBottom>
                            JSON Configuration Preview
                        </Typography>
                        <Paper
                            sx={{
                                p: 2,
                                backgroundColor: "#f5f5f5",
                                maxHeight: 400,
                                overflow: "auto",
                                mb: 3,
                            }}
                        >
                            <pre style={{ margin: 0, fontSize: "12px" }}>
                                {JSON.stringify(generateConfigJSON(), null, 2)}
                            </pre>
                        </Paper>

                        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={isLoading}
                            >
                                {isLoading ? "Saving..." : "Save Configuration"}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )
        },
    )

export default RulesEditorWithCRUD
